import React from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  getFeatureSchema,
  getBreadcrumbSchema
} from '../../utils/seoSchemas';

const steps = [
  'Bookings arrive automatically via email or the public booking form',
  'Jobs appear on the visual board organised by technician',
  'Drag and drop to assign or reassign jobs',
  'Technicians update status as work progresses',
  'Admin reviews completed work and sends payment requests',
  'Jobs move through the pipeline from booking to invoice'
];

const benefits = [
  'See all jobs at a glance with colour-coded status',
  'Reduce scheduling conflicts with real-time updates',
  'Track job history and technician workload',
  'Mobile-friendly for on-the-floor management',
  'Automatic notifications keep everyone informed'
];

const JobManagement = () => {
  const featureSchema = getFeatureSchema({
    name: 'Job Management',
    description: 'Visual job board with drag-and-drop scheduling for garage management. Assign technicians, track progress, and manage your entire workflow.',
    url: 'https://www.go-ops.digital/features/job-management'
  });
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.go-ops.digital/' },
    { name: 'Features', url: 'https://www.go-ops.digital/features' },
    { name: 'Job Management', url: 'https://www.go-ops.digital/features/job-management' }
  ]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet>
        <title>Job Management | Go Ops Features</title>
        <meta
          name="description"
          content="Visual job board with drag-and-drop scheduling for garage management. Assign technicians, track progress, and manage your entire workflow from one place."
        />
        <link rel="canonical" href="https://www.go-ops.digital/features/job-management" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="Job Management | Go Ops Features" />
        <meta
          property="og:description"
          content="Visual job board with drag-and-drop scheduling for garage management. Assign technicians, track progress, and manage your entire workflow."
        />
        <meta property="og:url" content="https://www.go-ops.digital/features/job-management" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify(featureSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Navigation */}
      <Box sx={{ bgcolor: '#2c3e50', py: 2 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{ color: 'white', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Go Ops
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="text" component={RouterLink} to="/features" sx={{ color: 'white' }}>
                Features
              </Button>
              <Button variant="text" component={RouterLink} to="/pricing" sx={{ color: 'white' }}>
                Pricing
              </Button>
              <Button variant="text" component={RouterLink} to="/contact" sx={{ color: 'white' }}>
                Contact
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/auth/login"
                sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
              >
                Login
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 6, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h1" component="h1" sx={{ fontSize: '2.5rem', fontWeight: 'bold', mb: 2 }}>
            Job Management
          </Typography>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
            Visual job board with drag-and-drop scheduling
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 4, lineHeight: 1.8 }}>
          Go Ops provides a visual job board that gives you complete visibility over your garage operations.
          Assign technicians with drag-and-drop, track progress in real-time, and manage your entire workflow
          from booking to invoice in one intuitive interface.
        </Typography>

        {/* How it works */}
        <Paper sx={{ p: 4, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
            How It Works
          </Typography>
          <List>
            {steps.map((step, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: '#ff6b35',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}
                  >
                    {index + 1}
                  </Box>
                </ListItemIcon>
                <ListItemText primary={step} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Benefits */}
        <Paper sx={{ p: 4, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
            Benefits
          </Typography>
          <List>
            {benefits.map((benefit, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#28a745' }} />
                </ListItemIcon>
                <ListItemText primary={benefit} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            See job management in action
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact"
              sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
            >
              Book a Demo
            </Button>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/features"
              sx={{ borderColor: '#2c3e50', color: '#2c3e50' }}
            >
              View All Features
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Go Ops. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Button component={RouterLink} to="/features" sx={{ color: 'white', textTransform: 'none' }}>
                Features
              </Button>
              <Button component={RouterLink} to="/pricing" sx={{ color: 'white', textTransform: 'none' }}>
                Pricing
              </Button>
              <Button component={RouterLink} to="/contact" sx={{ color: 'white', textTransform: 'none' }}>
                Contact
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default JobManagement;
